
import { defineComponent, onBeforeMount, onMounted, ref, watch } from "vue";
import { hideModal, modalHideListener } from "@/core/helpers/dom";
import { ApiBase, ApiAccount } from "@/core/api";
import mixin from "@/mixins";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "add-key-parameters-modal",
  props: {
    id: { type: Number, default: 0 },
    accountId: { type: Number, default: 0 },
  },
  components: {},
  emits: ["update-list", "reset-form"],
  setup(props, { emit }) {
    const formRef = ref<null | HTMLFormElement>(null);
    const addConnectionModalRef = ref<null | HTMLElement>(null);
    const submitButton = ref<HTMLElement | null>(null);
    const loading = ref<boolean>(false);
    const accountTypeOptions = ref([]);
    const { t } = useI18n();
    const {
      showValidateErrorMsg,
      showFormSubmitSuccessMsg,
      showServerErrorMsg,
    } = mixin();

    const formData = ref({
      id: props.id,
      account_id: props.accountId,
      account_number: "",
      type: "",
      verification_status: "",
      notes: "",
    });

    watch(
      () => props.id,
      (newVal) => {
        if (newVal) {
          getFormData(newVal);
        } else {
          resetForm();
        }
      }
    );

    const rules = ref({
      account_number: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
      type: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
      verification_status: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
    });

    const getFormData = (id) => {
      if (id) {
        loading.value = true;
        ApiAccount.getSocialConnectionInfo({ id: id })
          .then(({ data }) => {
            loading.value = false;
            if (data.code == 0) {
              let year = {
                fiscal_year: String(data.data.fiscal_year),
              };
              formData.value = { ...data.data, ...year };
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    };

    const getDropdownOptions = async () => {
      const { data } = await ApiBase.getTaggingData({
        short_key: "global_social_connection_type",
      });
      if (data.code == 0) {
        accountTypeOptions.value = data.data.items;
      }
    };

    const submit = () => {
      if (!formRef.value) {
        return;
      }

      formRef.value.validate((valid) => {
        if (valid) {
          loading.value = true;
          if (submitButton.value) {
            submitButton.value.setAttribute("data-kt-indicator", "on");
          }
          if (props.id) {
            editKeyarameters();
          } else {
            addKeyarameters();
          }
        } else {
          showValidateErrorMsg();
          return false;
        }
      });
    };

    const addKeyarameters = () => {
      ApiAccount.addSocialConnection(formData.value)
        .then(({ data }) => {
          loading.value = false;
          submitButton.value?.removeAttribute("data-kt-indicator");
          if (data.code == 0) {
            showFormSubmitSuccessMsg(() => {
              resetForm();
              hideModal(addConnectionModalRef.value);
              emit("update-list");
            });
          } else {
            showServerErrorMsg(data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const editKeyarameters = () => {
      ApiAccount.updateSocialConnection(formData.value)
        .then(({ data }) => {
          loading.value = false;
          submitButton.value?.removeAttribute("data-kt-indicator");
          if (data.code == 0) {
            showFormSubmitSuccessMsg(() => {
              resetForm();
              hideModal(addConnectionModalRef.value);
              emit("update-list");
            });
          } else {
            showServerErrorMsg(data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const handleDiscard = () => {
      hideModal(addConnectionModalRef.value);
      resetForm();
      emit("reset-form");
    };

    const resetForm = () => {
      formRef.value?.resetFields();
    };

    onMounted(() => {
      modalHideListener(addConnectionModalRef.value, () => {
        emit("reset-form");
      });
    });

    onBeforeMount(() => {
      getDropdownOptions();
    });

    return {
      loading,
      formData,
      rules,
      formRef,
      addConnectionModalRef,
      submitButton,
      accountTypeOptions,
      t,
      getFormData,
      submit,
      handleDiscard,
      resetForm,
    };
  },
});
